import AnnotationIssues from "./AnnotationIssues";
import { Fragment } from "react";
import { useBenchmarkTakeoverStore } from "src/stores/benchmarkTakeoverStore";

// Compare-stage panel: "Which option will you use?", the decision
// textarea, Back / Write Again / Finish actions, the "Thank you!" sticky note,
// and the refund fine print.

const CHOICES = [
  { value: "A", label: "Option A" },
  { value: "B", label: "Option B" },
  { value: "neither", label: "Neither" },
];

export default function PreferencePanel({ onBack, onFinished, busy }) {
  const canWriteAgain = useBenchmarkTakeoverStore((state) => state.canWriteAgain);
  const choice = useBenchmarkTakeoverStore((state) => state.choice);
  const decisionReason = useBenchmarkTakeoverStore((state) => state.decisionReason);
  const submitting = useBenchmarkTakeoverStore((state) => state.submitting);
  const submitError = useBenchmarkTakeoverStore((state) => state.submitError);
  const ratingClosedMessage = useBenchmarkTakeoverStore((state) => state.ratingClosedMessage);
  const setChoice = useBenchmarkTakeoverStore((state) => state.setChoice);
  const setDecisionReason = useBenchmarkTakeoverStore((state) => state.setDecisionReason);
  const submitRating = useBenchmarkTakeoverStore((state) => state.submitRating);
  const writeAgain = useBenchmarkTakeoverStore((state) => state.writeAgain);
  const finalReady = useBenchmarkTakeoverStore((state) => {
    const rated = (sample) => ["characters_dialogue", "earned_emotion", "shows_not_tells", "tension_momentum", "followed_direction", "sounds_like_me"].every((key) => state.scores[sample][key] >= 1);
    return !state.ratingClosedMessage && state.generationDone && rated("A") && rated("B") && !!state.choice;
  });

  const disabled = !finalReady || submitting || busy;
  const prefersNeither = choice === "neither";

  const handleFinish = async () => {
    const result = await submitRating();
    // The store already refuses a response for a session that closed or was
    // replaced meanwhile; still, only move a panel the user is looking at.
    if (result.ok && useBenchmarkTakeoverStore.getState().stage === "compare") onFinished();
  };

  const handleWriteAgain = () => {
    writeAgain();
  };

  return (
    <fieldset className="preference">
      <AnnotationIssues />
      <div
        className="save-choice-card"
        role="group"
        aria-labelledby="bt-save-choice-title"
      >
        <div
          className="choice-title"
          id="bt-save-choice-title"
        >
          Which option will you use?{" "}
          <span
            className="required-mark"
            aria-hidden="true"
          >
            *
          </span>
        </div>
        <div className="choice-row">
          {CHOICES.map(({ value, label }) => (
            <Fragment key={value}>
              <input
                type="radio"
                name="bt-saved-sample"
                id={`bt-prefer-${value}`}
                value={value}
                checked={choice === value}
                disabled={busy || submitting}
                onChange={() => setChoice(value)}
              />
              <label htmlFor={`bt-prefer-${value}`}>{label}</label>
            </Fragment>
          ))}
        </div>
      </div>
      <div className="decision-field">
        <label htmlFor="bt-decision-reason">What decided it for you?</label>
        <textarea
          id="bt-decision-reason"
          placeholder="A detail, tradeoff, missing quality, or gut reaction"
          maxLength={20000}
          value={decisionReason}
          disabled={busy || submitting}
          onChange={(event) => setDecisionReason(event.target.value)}
        ></textarea>
      </div>
      {submitError && (
        <div
          className="gate"
          role="alert"
        >
          {submitError}
        </div>
      )}
      <div className={`preference-actions${prefersNeither && canWriteAgain ? " has-write" : ""}`}>
        <button
          className="back"
          type="button"
          disabled={submitting || busy}
          onClick={onBack}
        >
          Back
        </button>
        {prefersNeither && canWriteAgain && (
          <button
            className="write-again"
            type="button"
            disabled={disabled}
            onClick={handleWriteAgain}
          >
            Write Again
          </button>
        )}
        <div
          className="finish-thanks"
          aria-live="polite"
        >
          <span>Thank you!</span>
        </div>
        <button
          className="submit"
          type="button"
          disabled={disabled}
          onClick={handleFinish}
        >
          Finish
        </button>
      </div>
      {!ratingClosedMessage && <p className="refund-fineprint">Finish this comparison to get your generation credits back. If you leave without finishing, the charge remains.</p>}
    </fieldset>
  );
}
